import { useMemo, useState } from 'react'
import { router } from '@inertiajs/react'
import type { FormDataConvertible, RequestPayload } from '@inertiajs/core'
import { Cable, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

export interface CustomMcpCredentialRef {
  name: string
  field: string
}

export interface CustomMcpServer {
  id: string
  name: string
  enabled: boolean
  transport: 'http' | 'stdio'
  url?: string
  command?: string
  args?: string[]
  credential_env?: Record<string, CustomMcpCredentialRef>
  auth?: {
    type: 'none' | 'bearer' | 'header' | 'basic'
    header?: string
    credential?: CustomMcpCredentialRef
    username?: CustomMcpCredentialRef
    password?: CustomMcpCredentialRef
  }
}

export interface CustomMcpCredential {
  name: string
  description?: string
  fields: string[]
}

interface Props {
  workerId: string
  servers: CustomMcpServer[]
  credentials: CustomMcpCredential[]
  error?: string
  canManage: boolean
}

interface FormState {
  id: string
  name: string
  transport: 'http' | 'stdio'
  url: string
  command: string
  args: string
  envBindings: string
  authType: 'none' | 'bearer' | 'header' | 'basic'
  header: string
  credential: string
  username: string
  password: string
}

const emptyForm: FormState = {
  id: '', name: '', transport: 'http', url: '', command: '', args: '', envBindings: '',
  authType: 'none', header: 'X-API-Key', credential: '', username: '', password: '',
}

const refValue = (ref?: CustomMcpCredentialRef) => ref ? `${ref.name}.${ref.field}` : ''

const parseRef = (value: string): CustomMcpCredentialRef | undefined => {
  const separator = value.indexOf('.')
  if (separator < 1) return undefined
  return { name: value.slice(0, separator), field: value.slice(separator + 1) }
}

function CredentialSelect({ value, onChange, options, placeholder }: {
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
  placeholder: string
}) {
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger className="w-full"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
      </SelectContent>
    </Select>
  )
}

export default function CustomMcpPanel({ workerId, servers, credentials, error, canManage }: Props) {
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const credentialOptions = useMemo(() => credentials.flatMap((credential) =>
    credential.fields.map((field) => ({
      value: `${credential.name}.${field}`,
      label: `${credential.name} · ${field}`,
    }))), [credentials])

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [field]: value }))

  const startCreate = () => {
    setEditingId(null)
    setForm(emptyForm)
    setOpen(true)
  }

  const startEdit = (server: CustomMcpServer) => {
    setEditingId(server.id)
    setForm({
      id: server.id,
      name: server.name,
      transport: server.transport,
      url: server.url ?? '',
      command: server.command ?? '',
      args: (server.args ?? []).join('\n'),
      envBindings: Object.entries(server.credential_env ?? {}).map(([name, ref]) => `${name}=${refValue(ref)}`).join('\n'),
      authType: server.auth?.type ?? 'none',
      header: server.auth?.header ?? 'X-API-Key',
      credential: refValue(server.auth?.credential),
      username: refValue(server.auth?.username),
      password: refValue(server.auth?.password),
    })
    setOpen(true)
  }

  const payload = (): RequestPayload => {
    const server: Record<string, FormDataConvertible> = {
      id: form.id || undefined,
      name: form.name.trim(),
      transport: form.transport,
    }
    if (form.transport === 'http') {
      const auth: Record<string, FormDataConvertible> = { type: form.authType }
      if (form.authType === 'bearer' || form.authType === 'header') {
        const credential = parseRef(form.credential)
        auth.credential = credential ? { name: credential.name, field: credential.field } : undefined
      }
      if (form.authType === 'header') auth.header = form.header.trim()
      if (form.authType === 'basic') {
        const username = parseRef(form.username)
        const password = parseRef(form.password)
        auth.username = username ? { name: username.name, field: username.field } : undefined
        auth.password = password ? { name: password.name, field: password.field } : undefined
      }
      server.url = form.url.trim()
      server.auth = auth
    } else {
      server.command = form.command.trim()
      server.args = form.args.split('\n').map((arg) => arg.trim()).filter(Boolean)
      server.credential_env = Object.fromEntries(form.envBindings.split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
        const separator = line.indexOf('=')
        const reference = parseRef(line.slice(separator + 1).trim())
        return [line.slice(0, separator).trim(), reference ? { name: reference.name, field: reference.field } : undefined]
      }).filter(([name, ref]) => name && ref))
    }
    return { server }
  }

  const save = () => {
    setSubmitting(true)
    const path = editingId
      ? `/dashboard/digital_workers/${workerId}/custom_mcp_servers/${editingId}`
      : `/dashboard/digital_workers/${workerId}/custom_mcp_servers`
    router.visit(path, {
      method: editingId ? 'patch' : 'post',
      data: payload(),
      preserveScroll: true,
      onSuccess: () => setOpen(false),
      onFinish: () => setSubmitting(false),
    })
  }

  const setEnabled = (server: CustomMcpServer, enabled: boolean) => {
    router.patch(`/dashboard/digital_workers/${workerId}/custom_mcp_servers/${server.id}`, {
      server: { enabled },
    }, { preserveScroll: true })
  }

  const remove = (server: CustomMcpServer) => {
    if (!window.confirm(`Remover o MCP “${server.name}” deste agente?`)) return
    router.delete(`/dashboard/digital_workers/${workerId}/custom_mcp_servers/${server.id}`, { preserveScroll: true })
  }

  const authNeedsCredential = form.authType === 'bearer' || form.authType === 'header'
  const requiredAuthReady = form.authType === 'none'
    || (authNeedsCredential && Boolean(form.credential))
    || (form.authType === 'basic' && Boolean(form.username) && Boolean(form.password))
  const canSave = form.name.trim() && (form.transport === 'http' ? form.url.trim() && requiredAuthReady : form.command.trim())

  return (
    <section className="space-y-3 rounded-xl border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-secondary"><Cable className="size-5 text-muted-foreground" /></div>
          <div>
            <h2 className="text-[14px] font-semibold text-foreground">MCP personalizado</h2>
            <p className="mt-0.5 text-[13px] text-muted-foreground">Servidores exclusivos deste agente. Segredos continuam nas Credenciais da Workstation.</p>
          </div>
        </div>
        {canManage && <Button size="sm" variant="outline" onClick={startCreate}><Plus className="size-4" /> Adicionar MCP</Button>}
      </div>

      {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-[13px] text-destructive">{error}</p>}
      {!error && servers.length === 0 && <p className="rounded-lg bg-muted/40 px-3 py-3 text-[13px] text-muted-foreground">Nenhum MCP personalizado configurado.</p>}

      {servers.length > 0 && (
        <div className="divide-y divide-border rounded-lg border border-border">
          {servers.map((server) => (
            <div key={server.id} className="flex items-center gap-3 px-3 py-2.5">
              <span className={`size-2 rounded-full ${server.enabled ? 'bg-success' : 'bg-muted-foreground/40'}`} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><span className="truncate text-[13px] font-medium">{server.name}</span><span className="text-[10px] uppercase tracking-wide text-muted-foreground">{server.transport}</span></div>
                <p className="truncate text-[12px] text-muted-foreground">{server.transport === 'http' ? server.url : [server.command, ...(server.args ?? [])].join(' ')}</p>
              </div>
              {canManage && (
                <div className="flex items-center gap-1">
                  <Switch checked={server.enabled} onCheckedChange={(enabled) => setEnabled(server, enabled)} aria-label={`Ativar ${server.name}`} />
                  <Button size="icon" variant="ghost" onClick={() => startEdit(server)} aria-label={`Editar ${server.name}`}><Pencil className="size-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(server)} aria-label={`Remover ${server.name}`}><Trash2 className="size-4" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-h-[88vh] overflow-y-auto sm:max-w-xl"
          onInteractOutside={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar MCP' : 'Adicionar MCP'}</DialogTitle>
            <DialogDescription>Configure o servidor; autenticação usa uma credencial já atribuída a este agente.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5"><Label htmlFor="mcp-name">Nome</Label><Input id="mcp-name" value={form.name} onChange={(event) => setField('name', event.target.value)} placeholder="Ex.: CRM interno" /></div>
            <div className="grid gap-1.5"><Label>Transporte</Label><Select value={form.transport} onValueChange={(value: 'http' | 'stdio') => setField('transport', value)}><SelectTrigger className="w-full"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="http">HTTP streaming</SelectItem><SelectItem value="stdio">STDIO</SelectItem></SelectContent></Select></div>

            {form.transport === 'http' ? (
              <>
                <div className="grid gap-1.5"><Label htmlFor="mcp-url">URL</Label><Input id="mcp-url" value={form.url} onChange={(event) => setField('url', event.target.value)} placeholder="https://mcp.exemplo.com/mcp" /></div>
                <div className="grid gap-1.5"><Label>Autenticação</Label><Select value={form.authType} onValueChange={(value: FormState['authType']) => setField('authType', value)}><SelectTrigger className="w-full"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Sem autenticação</SelectItem><SelectItem value="bearer">Bearer token</SelectItem><SelectItem value="header">Header com API key</SelectItem><SelectItem value="basic">Basic auth</SelectItem></SelectContent></Select></div>
                {authNeedsCredential && <div className="grid gap-1.5"><Label>Credencial</Label><CredentialSelect value={form.credential} onChange={(value) => setField('credential', value)} options={credentialOptions} placeholder="Selecionar credencial e campo" /></div>}
                {form.authType === 'header' && <div className="grid gap-1.5"><Label htmlFor="mcp-header">Nome do header</Label><Input id="mcp-header" value={form.header} onChange={(event) => setField('header', event.target.value)} placeholder="X-API-Key" /></div>}
                {form.authType === 'basic' && <><div className="grid gap-1.5"><Label>Usuário</Label><CredentialSelect value={form.username} onChange={(value) => setField('username', value)} options={credentialOptions} placeholder="Campo com usuário" /></div><div className="grid gap-1.5"><Label>Senha</Label><CredentialSelect value={form.password} onChange={(value) => setField('password', value)} options={credentialOptions} placeholder="Campo com senha" /></div></>}
              </>
            ) : (
              <>
                <div className="grid gap-1.5"><Label htmlFor="mcp-command">Comando</Label><Input id="mcp-command" value={form.command} onChange={(event) => setField('command', event.target.value)} placeholder="npx" /></div>
                <div className="grid gap-1.5"><Label htmlFor="mcp-args">Argumentos, um por linha</Label><Textarea id="mcp-args" value={form.args} onChange={(event) => setField('args', event.target.value)} placeholder={'-y\n@empresa/mcp-server'} rows={3} /></div>
                <div className="grid gap-1.5"><Label htmlFor="mcp-env">Variáveis de credencial</Label><Textarea id="mcp-env" value={form.envBindings} onChange={(event) => setField('envBindings', event.target.value)} placeholder="API_TOKEN=credencial.campo" rows={3} /><p className="text-[11px] text-muted-foreground">Uma por linha. O valor é resolvido pelo Fleet e nunca salvo aqui.</p></div>
              </>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button disabled={!canSave || submitting} onClick={save}>{submitting ? 'Salvando…' : 'Salvar MCP'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
